/**
 * Top-level tab switcher for the per-patient workspace.
 *
 * Owns the active tab, the cross-tab card reveal state, the **app-shell
 * Care Delivery AI chat**, and the **app-shell transcript-ingestion review
 * panel**. Both the chat and the review panel are rendered ONCE here, OUTSIDE
 * the `activeTab` conditional — so their state survives tab switches and they
 * never unmount. The {@link RevealCardContext} is provided here so that any
 * descendant (Documents, Chat, Sources) can trigger a Blueprint card reveal
 * without prop-drilling; a reveal switches to the Blueprint tab even from
 * another tab. The {@link ReviewControlContext} lets {@link SourcesPanel} open
 * the panel imperatively, so "View card details" inside a review does not
 * lose the in-progress adjudication. Nothing is logged (HIPAA).
 */
import React, { useCallback, useMemo, useRef, useState } from "react";
import MeasurementDashboard from "./assessments/MeasurementDashboard";
import BlueprintTab from "./blueprint/BlueprintTab";
import CareDeliveryChat from "./blueprint/CareDeliveryChat";
import DemoPatientResetButton from "./DemoPatientResetButton";
import DocumentsTab from "./documents/DocumentsTab";
import SourcesPanel from "./documents/SourcesPanel";
import ReviewPanel from "./documents/ReviewPanel";
import { sourceDisplayLabel } from "./documents/proposalLabels";
import ExploreTab from "./wiki/ExploreTab";
import { ErrorBoundary } from "./ErrorBoundary";
import { RevealCardContext } from "./RevealCardContext";
import { RevealSourceContext } from "./RevealSourceContext";
import { ChatControlContext, type ChatControl } from "./ChatControlContext";
import {
  ReviewControlContext,
  type ReviewControl,
} from "./ReviewControlContext";
import { useChatCollapse } from "../hooks/useChatCollapse";
import { SHOW_BLUEPRINT_EXPLORE_TABS } from "../featureFlags";
import type {
  SourceReveal,
  SourceRevealTarget,
} from "./blueprint/citationResolve";
import type { IngestionRun } from "../types";

interface AppContentProps {
  patientId: string;
  encounterId: string | null;
  userInfo: string | null;
  onLogout: () => void;
  sessionId: string;
  _accessToken: string | null;
}

/** The selectable workspace tabs. */
export type TabType =
  | "blueprint"
  | "assessments"
  | "documents"
  | "sources"
  | "explore";

/** Tailwind classes for an active vs. inactive tab button. */
function tabClass(active: boolean): string {
  return active
    ? "px-3 py-1 rounded bg-blue-100 text-blue-700 font-semibold cursor-pointer"
    : "px-3 py-1 rounded text-slate-600 hover:bg-slate-100 cursor-pointer";
}

export const AppContent: React.FC<AppContentProps> = ({ patientId }) => {
  // BLA-332: Blueprint/Explore are hidden (SHOW_BLUEPRINT_EXPLORE_TABS), so the
  // default landing tab is Documents (which auto-selects the patient's profile,
  // ADR-073), not the now-hidden Blueprint tab.
  // 2-Pane Split Mode State
  const [splitActive, setSplitActive] = useState(false);
  const [focusedPane, setFocusedPane] = useState<1 | 2>(1);
  const [pane1Tab, setPane1Tab] = useState<TabType>(
    SHOW_BLUEPRINT_EXPLORE_TABS ? "blueprint" : "documents",
  );
  const [pane2Tab, setPane2Tab] = useState<TabType>("sources");

  // Context menu for tab right-click
  const [tabContextMenu, setTabContextMenu] = useState<{
    x: number;
    y: number;
    tab: TabType;
  } | null>(null);

  const [revealTargetId, setRevealTargetId] = useState<string | null>(null);
  // Bumping this triggers a fresh data fetch on BlueprintTab via its
  // reloadTrigger prop after a chat turn with mutations (the `onTurnComplete` refresh).
  const [reloadNonce, setReloadNonce] = useState(0);
  // The transcript-ingestion review panel (lifted out of SourcesPanel so it
  // persists across tab switches — e.g. when the provider clicks
  // "View card details" inside the review).
  const [panelRun, setPanelRun] = useState<IngestionRun | null>(null);
  const chat = useChatCollapse();
  // The chat registers its "open pending review" handler here so descendants
  // (the Sources auto-ingest) can surface the review thread inline. The initial
  // no-op is only the placeholder before the chat registers its real handler.
  const openPendingReviewRef = useRef<() => void>(
    /* v8 ignore next */ () => undefined,
  );
  const registerControl = useCallback((openPendingReview: () => void): void => {
    openPendingReviewRef.current = openPendingReview;
  }, []);

  /**
   * Switch to the Blueprint tab and queue a card reveal.
   */
  const revealCard = useCallback(
    (targetId: string): void => {
      const targetTab = SHOW_BLUEPRINT_EXPLORE_TABS ? "blueprint" : "documents";
      if (splitActive) {
        if (focusedPane === 1) setPane1Tab(targetTab);
        else setPane2Tab(targetTab);
      } else {
        setPane1Tab(targetTab);
      }
      setRevealTargetId(targetId);
    },
    [splitActive, focusedPane],
  );

  const clearReveal = useCallback((): void => {
    setRevealTargetId(null);
  }, []);

  // Cross-tab source-citation reveal ("Go to source"):
  // For source citations: activates 2-pane split mode (if single view), mounting
  // Sources in Pane 2 and focusing it while preserving Pane 1 on current tab.
  // For derivative citations: opens in Documents (Pane 2 if split & focused on 2, else Pane 1).
  const [sourceReveal, setSourceReveal] = useState<SourceReveal | null>(null);
  const revealKeyRef = useRef(0);
  const revealSource = useCallback(
    (target: SourceRevealTarget): void => {
      revealKeyRef.current += 1;
      const revealObj = { target, key: revealKeyRef.current };
      setSourceReveal(revealObj);

      if (target.openInEditor) {
        // Derivative citation -> documents
        if (splitActive) {
          if (focusedPane === 2) {
            setPane2Tab("documents");
          } else {
            setPane1Tab("documents");
          }
        } else {
          setPane1Tab("documents");
        }
      } else {
        // Source citation -> sources
        if (!splitActive) {
          setSplitActive(true);
        }
        setPane2Tab("sources");
        setFocusedPane(2);
      }
    },
    [splitActive, focusedPane],
  );
  const clearSourceReveal = useCallback((): void => {
    setSourceReveal(null);
  }, []);

  // Close split mode and maximize the other pane
  const closePane = useCallback(
    (paneNum: 1 | 2): void => {
      if (paneNum === 1) {
        // Pane 1 closes: Pane 2 becomes the single pane view
        setPane1Tab(pane2Tab);
      }
      setSplitActive(false);
      setFocusedPane(1);
    },
    [pane2Tab],
  );

  // Tab selection: replaces whichever pane is currently in focus!
  const selectTab = useCallback(
    (tab: TabType): void => {
      setSourceReveal(null);
      if (!splitActive) {
        setPane1Tab(tab);
      } else {
        if (focusedPane === 1) {
          setPane1Tab(tab);
        } else {
          setPane2Tab(tab);
        }
      }
    },
    [splitActive, focusedPane],
  );

  // Open a specific tab in second pane (split mode)
  const openInSecondPane = useCallback((tab: TabType): void => {
    setSplitActive(true);
    setPane2Tab(tab);
    setFocusedPane(2);
    setTabContextMenu(null);
  }, []);

  /**
   * Tab-aware post-turn refresh.
   */
  const handleTurnComplete = useCallback((): void => {
    setSourceReveal(null);
    setReloadNonce((n) => n + 1);
  }, []);

  // Opening a pending review must also expand a collapsed chat so the inline diff is visible.
  const { collapsed, toggle } = chat;
  const chatControl = useMemo<ChatControl>(
    () => ({
      openPendingReview: (): void => {
        if (collapsed) toggle();
        openPendingReviewRef.current();
      },
    }),
    [collapsed, toggle],
  );

  /** Close (and clear) the transcript-ingestion review panel. */
  const closePanel = useCallback((): void => {
    setPanelRun(null);
  }, []);

  const reviewControl = useMemo<ReviewControl>(
    () => ({
      openReview: (run: IngestionRun): void => {
        setPanelRun(run);
      },
    }),
    [],
  );

  // Helper to render pane content based on TabType
  const renderPaneContent = (
    tab: TabType,
    paneIndex: 1 | 2,
  ): React.ReactElement | null => {
    if (SHOW_BLUEPRINT_EXPLORE_TABS && tab === "blueprint") {
      return (
        <div className="flex-1 overflow-hidden">
          <BlueprintTab
            key={`blueprint-${paneIndex}`}
            patientId={patientId}
            revealTargetId={revealTargetId}
            onRevealed={clearReveal}
            reloadTrigger={reloadNonce}
          />
        </div>
      );
    }
    if (tab === "documents") {
      return (
        <div className="flex-1 overflow-hidden">
          <DocumentsTab
            key={`documents-${paneIndex}`}
            patientId={patientId}
            reveal={sourceReveal}
            onClearReveal={clearSourceReveal}
          />
        </div>
      );
    }
    if (tab === "sources") {
      return (
        <div className="flex-1 overflow-hidden">
          <SourcesPanel
            key={`sources-${paneIndex}`}
            patientId={patientId}
            reveal={sourceReveal}
            onClearReveal={clearSourceReveal}
          />
        </div>
      );
    }
    if (SHOW_BLUEPRINT_EXPLORE_TABS && tab === "explore") {
      return (
        <div className="flex-1 overflow-hidden">
          <ExploreTab key={`explore-${paneIndex}`} patientId={patientId} />
        </div>
      );
    }
    return (
      <div className="flex-1 p-4 overflow-auto">
        <MeasurementDashboard
          key={`assessments-${paneIndex}`}
          patientId={patientId}
        />
      </div>
    );
  };

  // Determine top tab styling for split mode
  const getTabButtonClass = (tab: TabType): string => {
    if (!splitActive) {
      return tabClass(pane1Tab === tab);
    }
    const isP1 = pane1Tab === tab;
    const isP2 = pane2Tab === tab;
    const isFocused =
      (focusedPane === 1 && isP1) || (focusedPane === 2 && isP2);
    const isCoexisting =
      (focusedPane === 1 && isP2) || (focusedPane === 2 && isP1);

    if (isFocused) {
      return "px-3 py-1 rounded bg-blue-600 text-white font-semibold cursor-pointer shadow-xs";
    }
    if (isCoexisting) {
      return "px-3 py-1 rounded bg-blue-100 text-blue-900 border border-blue-300 font-medium cursor-pointer";
    }
    return "px-3 py-1 rounded text-slate-600 hover:bg-slate-100 cursor-pointer";
  };

  const labelForTab = (tab: TabType): string => {
    switch (tab) {
      case "documents":
        return "Documents";
      case "sources":
        return "Sources";
      case "assessments":
        return "Assessments";
      case "blueprint":
        return "Blueprint";
      case "explore":
        return "Explore";
    }
  };

  return (
    <RevealCardContext.Provider value={revealCard}>
      <RevealSourceContext.Provider value={revealSource}>
        <ChatControlContext.Provider value={chatControl}>
          <ReviewControlContext.Provider value={reviewControl}>
            <div
              className="flex h-full"
              onClick={() => {
                if (tabContextMenu) setTabContextMenu(null);
              }}
            >
              <div className="flex flex-col flex-1 min-w-0">
                <div className="border-b px-4 py-2 flex items-center justify-between shrink-0 bg-white">
                  <div className="flex items-center space-x-2">
                    <nav
                      className="flex space-x-2"
                      aria-label="Workspace primary tabs"
                    >
                      {SHOW_BLUEPRINT_EXPLORE_TABS && (
                        <button
                          data-testid="tab-blueprint"
                          className={getTabButtonClass("blueprint")}
                          onClick={() => selectTab("blueprint")}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            setTabContextMenu({
                              x: e.clientX,
                              y: e.clientY,
                              tab: "blueprint",
                            });
                          }}
                        >
                          Blueprint
                        </button>
                      )}
                      <button
                        data-testid="tab-documents"
                        className={getTabButtonClass("documents")}
                        onClick={() => selectTab("documents")}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setTabContextMenu({
                            x: e.clientX,
                            y: e.clientY,
                            tab: "documents",
                          });
                        }}
                      >
                        Documents
                      </button>
                      <button
                        data-testid="tab-sources"
                        className={getTabButtonClass("sources")}
                        onClick={() => selectTab("sources")}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setTabContextMenu({
                            x: e.clientX,
                            y: e.clientY,
                            tab: "sources",
                          });
                        }}
                      >
                        Sources
                      </button>
                      {SHOW_BLUEPRINT_EXPLORE_TABS && (
                        <button
                          data-testid="tab-explore"
                          className={getTabButtonClass("explore")}
                          onClick={() => selectTab("explore")}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            setTabContextMenu({
                              x: e.clientX,
                              y: e.clientY,
                              tab: "explore",
                            });
                          }}
                        >
                          Explore
                        </button>
                      )}
                      <button
                        data-testid="tab-assessments"
                        className={getTabButtonClass("assessments")}
                        onClick={() => selectTab("assessments")}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setTabContextMenu({
                            x: e.clientX,
                            y: e.clientY,
                            tab: "assessments",
                          });
                        }}
                      >
                        Assessments
                      </button>
                    </nav>

                    <div className="h-4 w-[1px] bg-slate-200 mx-2" />

                    {/* Split View Toggle */}
                    <button
                      type="button"
                      data-testid="toggle-split-view"
                      onClick={() => {
                        if (!splitActive) {
                          setSplitActive(true);
                          setPane2Tab(
                            pane1Tab === "sources" ? "documents" : "sources",
                          );
                          setFocusedPane(2);
                        } else {
                          setSplitActive(false);
                          setFocusedPane(1);
                        }
                      }}
                      className={`text-xs px-2.5 py-1 rounded font-medium border flex items-center gap-1 transition-colors cursor-pointer ${
                        splitActive
                          ? "bg-blue-50 border-blue-300 text-blue-700"
                          : "border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                      title={
                        splitActive
                          ? "Collapse to single view"
                          : "Open 2-pane split view"
                      }
                    >
                      <span>◫</span>
                      <span>
                        {splitActive ? "Split: 2 Panes" : "Split View"}
                      </span>
                    </button>
                  </div>

                  <DemoPatientResetButton
                    patientId={patientId}
                    onReset={handleTurnComplete}
                  />
                </div>

                {/* Right-click Tab Context Menu */}
                {tabContextMenu && (
                  <div
                    role="menu"
                    data-testid="tab-context-menu"
                    style={{
                      position: "fixed",
                      top: tabContextMenu.y,
                      left: tabContextMenu.x,
                      zIndex: 60,
                    }}
                    className="w-56 rounded-lg border border-slate-200 bg-white py-1 shadow-lg text-xs"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="px-3 py-1 font-semibold text-slate-700 border-b border-slate-100">
                      {labelForTab(tabContextMenu.tab)} Tab
                    </div>
                    <button
                      type="button"
                      data-testid="tab-context-split-action"
                      onClick={() => openInSecondPane(tabContextMenu.tab)}
                      className="w-full text-left px-3 py-1.5 hover:bg-blue-50 hover:text-blue-700 font-medium text-slate-700 flex items-center gap-1.5 cursor-pointer"
                    >
                      <span>◫</span>
                      <span>
                        Open {labelForTab(tabContextMenu.tab)} in a second pane
                      </span>
                    </button>
                  </div>
                )}

                {/* Workspace Container: Single or 2-Pane Split */}
                <div
                  data-testid="split-workspace-grid"
                  className={
                    !splitActive
                      ? "flex-1 flex flex-col overflow-hidden"
                      : "flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-300 bg-slate-100"
                  }
                >
                  {/* Pane 1 */}
                  <div
                    data-testid="pane-1"
                    onClick={() => setFocusedPane(1)}
                    className={
                      !splitActive
                        ? "flex-1 w-full flex flex-col min-w-0 bg-white overflow-hidden"
                        : "flex flex-col min-w-0 bg-white overflow-hidden"
                    }
                  >
                    {splitActive && (
                      <div
                        data-testid="pane-1-subheader"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFocusedPane(1);
                        }}
                        className={`px-3 py-1.5 flex items-center justify-between text-xs shrink-0 cursor-pointer border-b transition-colors ${
                          focusedPane === 1
                            ? "bg-blue-50/70 border-blue-200"
                            : "bg-slate-100 border-slate-200 text-slate-600"
                        }`}
                      >
                        <span className="font-semibold text-slate-800">
                          {labelForTab(pane1Tab)}
                        </span>
                        <button
                          type="button"
                          data-testid="close-pane-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            closePane(1);
                          }}
                          className="text-slate-400 hover:text-rose-600 font-bold p-0.5 rounded cursor-pointer"
                          title="Close pane"
                          aria-label="Close pane 1"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                    <div className="flex-1 overflow-hidden flex flex-col">
                      {renderPaneContent(pane1Tab, 1)}
                    </div>
                  </div>

                  {/* Pane 2 */}
                  <div
                    data-testid="pane-2"
                    onClick={() => setFocusedPane(2)}
                    className={
                      !splitActive
                        ? "hidden"
                        : "flex flex-col min-w-0 bg-white overflow-hidden"
                    }
                  >
                    {splitActive && (
                      <div
                        data-testid="pane-2-subheader"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFocusedPane(2);
                        }}
                        className={`px-3 py-1.5 flex items-center justify-between text-xs shrink-0 cursor-pointer border-b transition-colors ${
                          focusedPane === 2
                            ? "bg-blue-50/70 border-blue-200"
                            : "bg-slate-100 border-slate-200 text-slate-600"
                        }`}
                      >
                        <span className="font-semibold text-slate-800">
                          {labelForTab(pane2Tab)}
                        </span>
                        <button
                          type="button"
                          data-testid="close-pane-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            closePane(2);
                          }}
                          className="text-slate-400 hover:text-rose-600 font-bold p-0.5 rounded cursor-pointer"
                          title="Close pane"
                          aria-label="Close pane 2"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                    <div className="flex-1 overflow-hidden flex flex-col">
                      {renderPaneContent(pane2Tab, 2)}
                    </div>
                  </div>
                </div>
              </div>

              <ErrorBoundary context="care-delivery-chat">
                <CareDeliveryChat
                  patientId={patientId}
                  onTurnComplete={handleTurnComplete}
                  collapsed={chat.collapsed}
                  onToggleCollapse={chat.toggle}
                  onRegisterControl={registerControl}
                />
              </ErrorBoundary>
            </div>

            {/*
            The transcript-ingestion review panel is rendered ONCE here, outside
            the activeTab conditional, so it persists when the provider switches
            tabs (e.g. "View card details" inside the review). SourcesPanel
            opens it via ReviewControlContext.openReview(run); closing it clears
            panelRun back to null.
          */}
            {panelRun !== null && (
              <ReviewPanel
                runId={panelRun.id}
                sourceTitle={
                  sourceDisplayLabel(panelRun) ?? "the source document"
                }
                onClose={closePanel}
                onResolved={closePanel}
              />
            )}
          </ReviewControlContext.Provider>
        </ChatControlContext.Provider>
      </RevealSourceContext.Provider>
    </RevealCardContext.Provider>
  );
};

export default AppContent;
